import { useMemo } from 'react'
import { useAuthStore } from '../stores/authStore'
import { useNavigate } from 'react-router-dom'
import type { ApiClient, ApiError } from '../types'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

export function useApi(): ApiClient & { fetchWithAuth: <T = unknown>(endpoint: string, options?: RequestInit, retryCount?: number, returnRaw?: boolean) => Promise<T | Response> } {
  const getAuthHeaders = useAuthStore((state) => state.getAuthHeaders)
  const getAccessToken = useAuthStore((state) => state.getAccessToken)
  const refreshToken = useAuthStore((state) => state.refreshToken)
  const logout = useAuthStore((state) => state.logout)
  const navigate = useNavigate()

  return useMemo(() => {
    const fetchWithAuth = async <T = unknown>(endpoint: string, options: RequestInit = {}, retryCount = 0, returnRaw = false): Promise<T | Response> => {
      const token = getAccessToken()

      if (!token) {
        throw new Error('認証トークンが見つかりません。再ログインしてください。')
      }

      const headers: Record<string, string> = {
        ...getAuthHeaders(),
        ...(options.headers as Record<string, string>),
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers,
      })

      // 401エラー（認証エラー）の場合
      if (response.status === 401 && retryCount === 0) {
        console.warn('認証トークンが無効です。リフレッシュを試みます...')

        try {
          // トークンをリフレッシュ
          await refreshToken()
          console.log('トークンのリフレッシュに成功しました')

          // リフレッシュ後、同じリクエストを再試行
          return fetchWithAuth<T>(endpoint, options, retryCount + 1, returnRaw)
        } catch (refreshError) {
          console.error('トークンのリフレッシュに失敗しました:', refreshError)

          // ネットワークエラーの場合はログアウトせずエラーだけ投げる
          // （スリープ復帰直後など一時的な接続障害の可能性があるため）
          if (!navigator.onLine || (refreshError instanceof Error && refreshError.message === 'Network error')) {
            throw new Error('ネットワーク接続を確認してください')
          }

          // セッション無効（refresh token 期限切れ等）→ ログアウト
          logout()
          navigate('/login', { replace: true })
          throw new Error('セッションの有効期限が切れました。再度ログインしてください。')
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'リクエストに失敗しました' }))
        const error = new Error(errorData.message || errorData.error || `HTTPエラー: ${response.status}`) as ApiError
        error.status = response.status
        error.response = {
          status: response.status,
          data: errorData
        }
        throw error
      }

      // Rawレスポンスを返す場合（CSVダウンロードなど）
      if (returnRaw) {
        return response
      }

      return response.json() as Promise<T>
    }

    const get = <T = unknown>(endpoint: string, options: RequestInit = {}): Promise<T> => {
      return fetchWithAuth<T>(endpoint, {
        ...options,
        method: 'GET',
      }) as Promise<T>
    }

    const getRaw = (endpoint: string, options: RequestInit = {}): Promise<Response> => {
      return fetchWithAuth(endpoint, {
        ...options,
        method: 'GET',
      }, 0, true) as Promise<Response>
    }

    const post = <T = unknown>(endpoint: string, data?: unknown, options: RequestInit = {}): Promise<T> => {
      return fetchWithAuth<T>(endpoint, {
        ...options,
        method: 'POST',
        body: JSON.stringify(data),
      }) as Promise<T>
    }

    const put = <T = unknown>(endpoint: string, data?: unknown, options: RequestInit = {}): Promise<T> => {
      return fetchWithAuth<T>(endpoint, {
        ...options,
        method: 'PUT',
        body: JSON.stringify(data),
      }) as Promise<T>
    }

    const del = <T = unknown>(endpoint: string, options: RequestInit = {}): Promise<T> => {
      return fetchWithAuth<T>(endpoint, {
        ...options,
        method: 'DELETE',
      }) as Promise<T>
    }

    const uploadFile = async <T = unknown>(endpoint: string, formData: FormData, options: RequestInit = {}, retryCount = 0): Promise<T> => {
      // FormDataの場合、Content-Typeヘッダーを設定せずブラウザに自動設定させる
      const token = getAccessToken()

      if (!token) {
        throw new Error('認証トークンが見つかりません。再ログインしてください。')
      }

      const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
        // Content-Typeは明示的に設定しない（ブラウザがboundaryを含めて自動設定）
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        method: 'POST',
        headers,
        body: formData,
      })

      // 401エラー（認証エラー）の場合
      if (response.status === 401 && retryCount === 0) {
        console.warn('認証トークンが無効です。リフレッシュを試みます...')

        try {
          // トークンをリフレッシュ
          await refreshToken()
          console.log('トークンのリフレッシュに成功しました')

          // リフレッシュ後、同じリクエストを再試行
          return uploadFile<T>(endpoint, formData, options, retryCount + 1)
        } catch (refreshError) {
          console.error('トークンのリフレッシュに失敗しました:', refreshError)

          if (!navigator.onLine || (refreshError instanceof Error && refreshError.message === 'Network error')) {
            throw new Error('ネットワーク接続を確認してください')
          }

          logout()
          navigate('/login', { replace: true })
          throw new Error('セッションの有効期限が切れました。再度ログインしてください。')
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'リクエストに失敗しました' }))
        const error = new Error(errorData.message || errorData.error || `HTTPエラー: ${response.status}`) as ApiError
        error.status = response.status
        error.response = {
          status: response.status,
          data: errorData
        }
        throw error
      }

      return response.json() as Promise<T>
    }

    return {
      get,
      getRaw,
      post,
      put,
      delete: del,
      uploadFile,
      fetchWithAuth,
    }
  }, [getAuthHeaders, getAccessToken, refreshToken, logout, navigate])
}
